import { categorizeDataType } from './filterUtils';

export type InferColumn = { name: string; dataType: string };
export type InferTable = {
  schema: string;
  name: string;
  columns: InferColumn[];
  primaryKey?: string;
};
export type InferSource = {
  schema: string;
  table: string;
  name: string;
  dataType: string;
};
export type InferResult = { schema: string; table: string; column: string } | null;

/** Lightweight table reference — what `findCandidateTables` needs. */
export type TableNameRef = { schema: string; name: string };

/**
 * Type-level gate for offering a loose-FK *declaration* on a column.
 * Foreign keys reference identity columns (integers, UUIDs/strings). A
 * datetime or boolean column is never a meaningful FK source, so the
 * "Declare loose FK" context-menu entries are hidden for those. Editing an
 * already-declared loose FK stays allowed regardless of column type.
 */
export function isLooseFkCandidateType(dataType: string | undefined | null): boolean {
  const category = categorizeDataType(dataType ?? '');
  return category !== 'datetime' && category !== 'boolean';
}

const ID_SUFFIX_RE = /^(.+?)(?:_ids|_uuids|_id|_uuid|_fk|Ids|Uuids|Id|Uuid)$/;

const baseName = (column: string): string => {
  const m = column.match(ID_SUFFIX_RE);
  return m?.[1] ?? column;
};

const candidateNames = (base: string): string[] => {
  const lower = base.toLowerCase();
  const variants = new Set<string>([lower]);
  variants.add(`${lower}s`);
  variants.add(`${lower}es`);
  if (lower.endsWith('y')) variants.add(`${lower.slice(0, -1)}ies`);
  return [...variants];
};

const PREFIXES = ['t_', 'tbl_', 'app_'];
const SUFFIXES = ['_t', '_tbl'];

const matchesName = (tableName: string, candidates: string[]): boolean => {
  const lower = tableName.toLowerCase();
  if (candidates.includes(lower)) return true;
  for (const c of candidates) {
    if (PREFIXES.some((p) => lower === `${p}${c}`)) return true;
    if (SUFFIXES.some((s) => lower === `${c}${s}`)) return true;
  }
  return false;
};

const SCHEMA_FALLBACKS = ['dbo', 'public'];

const schemaPriority = (sourceSchema: string, candidate: string): number => {
  if (candidate === sourceSchema) return 0;
  const i = SCHEMA_FALLBACKS.indexOf(candidate);
  if (i >= 0) return 1 + i;
  return 99;
};

/**
 * Find target tables whose name matches the source column's base name
 * (after stripping common ID suffixes like `_id`, `Id`, `_uuid`).
 * Sorted by schema priority (same schema > dbo/public > others).
 *
 * Pure name-matching — does not require column information, so it can be
 * called against the lightweight schema-only table list.
 */
export function findCandidateTables(
  source: Pick<InferSource, 'schema' | 'name'>,
  tables: readonly TableNameRef[],
): TableNameRef[] {
  const base = baseName(source.name);
  if (base === source.name && !source.name.toLowerCase().includes('id')) {
    return [];
  }
  const candidates = candidateNames(base);
  const matches = tables.filter((t) => matchesName(t.name, candidates));
  if (matches.length === 0) return [];
  const sorted = [...matches].sort(
    (a, b) => schemaPriority(source.schema, a.schema) - schemaPriority(source.schema, b.schema),
  );
  return sorted;
}

/**
 * Pick the best target column on `table` for a source column of type `sourceType`.
 * Priority: explicit primary key > column literally named "id" > column whose
 * data type matches `sourceType` > null.
 */
export function pickTargetColumn(
  table: { columns: InferColumn[]; primaryKey?: string },
  sourceType: string,
): string | null {
  if (table.primaryKey) return table.primaryKey;
  const idCol = table.columns.find((c) => c.name.toLowerCase() === 'id');
  if (idCol) return idCol.name;
  const typeMatch = table.columns.find(
    (c) => c.dataType.toLowerCase() === sourceType.toLowerCase(),
  );
  return typeMatch?.name ?? null;
}

export function inferLooseRelationship(source: InferSource, tables: InferTable[]): InferResult {
  const candidates = findCandidateTables(
    source,
    tables.map((t) => ({ schema: t.schema, name: t.name })),
  );
  for (const c of candidates) {
    const tbl = tables.find((t) => t.schema === c.schema && t.name === c.name);
    if (!tbl) continue;
    const col = pickTargetColumn(tbl, source.dataType);
    if (col) return { schema: tbl.schema, table: tbl.name, column: col };
  }
  return null;
}
